import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Link as LinkIcon,
  Image,
  Table,
  Save,
  Loader2,
  Upload,
} from 'lucide-react'
import { useState, useCallback, useEffect, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import { useUploadKnowledgeBaseImage } from '@/hooks'
import { cn } from '@/lib/utils'

interface EditorProps {
  initialContent?: string
  initialTitle?: string
  /** 文档 ID，用于关联上传的图片 */
  documentId?: string
  /** 知识库 ID，用于关联上传的图片 */
  knowledgeBaseId?: string
  onChange?: (content: string) => void
  onTitleChange?: (title: string) => void
  onSave?: (title: string, content: string) => void
  isSaving?: boolean
  autoSave?: boolean
  autoSaveInterval?: number
  className?: string
  placeholder?: string
}

/**
 * 富文本编辑器组件
 * 基于 Markdown 的简单编辑器，支持常用格式化功能
 * 支持图片拖拽上传和粘贴上传
 * 
 * TODO: 升级到 TipTap 编辑器以获得更好的富文本编辑体验
 */
export function Editor({
  initialContent = '',
  initialTitle = '',
  documentId,
  knowledgeBaseId,
  onChange,
  onTitleChange,
  onSave,
  isSaving = false,
  autoSave = true,
  autoSaveInterval = 30000, // 30 seconds
  className,
  placeholder = '开始编写文档...',
}: EditorProps) {
  const [title, setTitle] = useState(initialTitle)
  const [content, setContent] = useState(initialContent)
  const [lastSavedContent, setLastSavedContent] = useState(initialContent)
  const [lastSavedTitle, setLastSavedTitle] = useState(initialTitle)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const toast = useToast()
  const uploadImage = useUploadKnowledgeBaseImage()

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(content !== lastSavedContent || title !== lastSavedTitle)
  }, [content, title, lastSavedContent, lastSavedTitle])



  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const handleTitleChange = useCallback(
    (newTitle: string) => {
      setTitle(newTitle)
      onTitleChange?.(newTitle)
    },
    [onTitleChange]
  )

  const handleContentChange = useCallback(
    (newContent: string) => {
      setContent(newContent)
      onChange?.(newContent)
    },
    [onChange]
  )

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(title, content)
      setLastSavedContent(content)
      setLastSavedTitle(title)
    }
  }, [onSave, title, content])

  // Auto-save functionality
  useEffect(() => {
    if (!autoSave || !hasUnsavedChanges || !onSave) { return }

    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current)
    }

    autoSaveTimerRef.current = setTimeout(() => {
      handleSave()
    }, autoSaveInterval)

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  }, [content, title, autoSave, hasUnsavedChanges, autoSaveInterval, handleSave, onSave])

  // Handle image upload
  const handleImageUpload = useCallback(
    async (file: File) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('只支持上传图片文件')
        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('图片大小不能超过 10MB')
        return
      }

      setIsUploading(true)
      try {
        const result = await uploadImage.mutateAsync({
          file,
          documentId,
          knowledgeBaseId,
        })

        // Insert image markdown at cursor position
        const textarea = textareaRef.current
        if (textarea) {
          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          const imageMarkdown = `![${file.name}](${result.url})`
          const newContent =
            content.substring(0, start) + imageMarkdown + content.substring(end)
          handleContentChange(newContent)

          // Move cursor after the inserted image
          setTimeout(() => {
            textarea.focus()
            const newCursorPos = start + imageMarkdown.length
            textarea.setSelectionRange(newCursorPos, newCursorPos)
          }, 0)
        }

        toast.success('图片上传成功')
      } catch (error) {
        console.error('Image upload failed:', error)
        toast.error(error instanceof Error ? error.message : '图片上传失败')
      } finally {
        setIsUploading(false)
      }
    },
    [content, documentId, knowledgeBaseId, handleContentChange, uploadImage, toast]
  )

  // Handle file input change
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (files && files.length > 0) {
        void handleImageUpload(files[0])
      }
      // Reset input value to allow uploading the same file again
      e.target.value = ''
    },
    [handleImageUpload]
  )

  // Handle drag events
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const files = e.dataTransfer.files
      if (files.length > 0) {
        // Find the first image file
        const imageFile = Array.from(files).find((file) =>
          file.type.startsWith('image/')
        )
        if (imageFile) {
          void handleImageUpload(imageFile)
        } else {
          toast.error('只支持上传图片文件')
        }
      }
    },
    [handleImageUpload, toast]
  )

  // Handle paste event for images
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData.items
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) {
            void handleImageUpload(file)
          }
          return
        }
      }
    },
    [handleImageUpload]
  )

  // Open file picker for image upload
  const handleImageButtonClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Insert text at cursor position
  const insertText = useCallback(
    (before: string, after: string = '') => {
      const textarea = textareaRef.current
      if (!textarea) { return }

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = content.substring(start, end)
      const newContent =
        content.substring(0, start) + before + selectedText + after + content.substring(end)

      handleContentChange(newContent)

      // Restore cursor position
      setTimeout(() => {
        textarea.focus()
        const newCursorPos = start + before.length + selectedText.length
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      }, 0)
    },
    [content, handleContentChange]
  )

  // Toolbar actions
  const toolbarActions = [
    { icon: Bold, label: '粗体', action: () => insertText('**', '**'), shortcut: '⌘B' },
    { icon: Italic, label: '斜体', action: () => insertText('*', '*'), shortcut: '⌘I' },
    { icon: Code, label: '代码', action: () => insertText('`', '`'), shortcut: '⌘E' },
    { type: 'separator' as const },
    { icon: Heading1, label: '标题1', action: () => insertText('# ') },
    { icon: Heading2, label: '标题2', action: () => insertText('## ') },
    { icon: Heading3, label: '标题3', action: () => insertText('### ') },
    { type: 'separator' as const },
    { icon: List, label: '无序列表', action: () => insertText('- ') },
    { icon: ListOrdered, label: '有序列表', action: () => insertText('1. ') },
    { type: 'separator' as const },
    { icon: LinkIcon, label: '链接', action: () => insertText('[', '](url)') },
    { icon: Image, label: '图片', action: handleImageButtonClick, isUpload: true },
    { icon: Table, label: '表格', action: () => insertText('\n| 列1 | 列2 |\n|-----|-----|\n| 内容 | 内容 |\n') },
  ]

  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault()
            insertText('**', '**')
            break
          case 'i':
            e.preventDefault()
            insertText('*', '*')
            break
          case 'e':
            e.preventDefault()
            insertText('`', '`')
            break
          case 's':
            e.preventDefault()
            handleSave()
            break
        }
      }
    },
    [insertText, handleSave]
  )

  return (
    <div
      className={cn('flex h-full flex-col relative', className)}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/10 border-2 border-dashed border-primary rounded-lg">
          <div className="flex flex-col items-center gap-2 text-primary">
            <Upload className="h-12 w-12" />
            <span className="text-lg font-medium">拖放图片到此处上传</span>
          </div>
        </div>
      )}

      {/* Upload progress overlay */}
      {isUploading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">正在上传图片...</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b bg-muted/30 px-2 py-1">
        {toolbarActions.map((action, index) =>
          action.type === 'separator' ? (
            <div key={index} className="mx-1 h-6 w-px bg-border" />
          ) : (
            <Button
              key={index}
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={action.action}
              title={action.label + (action.shortcut ? ` (${action.shortcut})` : '') + (action.isUpload ? ' (支持拖拽/粘贴)' : '')}
              disabled={isUploading}
            >
              <action.icon className="h-4 w-4" />
            </Button>
          )
        )}

        <div className="flex-1" />

        {/* Save Status */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>保存中...</span>
            </>
          ) : hasUnsavedChanges ? (
            <span>未保存</span>
          ) : (
            <span>已保存</span>
          )}
        </div>

        {/* Save Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !hasUnsavedChanges}
        >
          <Save className="mr-2 h-4 w-4" />
          保存
        </Button>
      </div>

      {/* Title Input */}
      <div className="border-b px-4 py-3">
        <input
          type="text"
          value={title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="文档标题"
          className="w-full border-0 bg-transparent text-2xl font-bold focus:outline-none"
        />
      </div>

      {/* Content Editor */}
      <div className="flex-1 overflow-hidden">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="h-full w-full resize-none border-0 bg-transparent p-4 font-mono text-sm focus:outline-none"
          spellCheck={false}
        />
      </div>
    </div>
  )
}
